import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  Gift,
  HandCoins,
  InfoIcon,
} from 'lucide-react'

import { ApiError } from '../../../lib/api'
import type { AgentRecord } from '../../../types/agents'
import type { AssignedAgent, ExchangePackRecord, ProgramRecord } from '../../../types/programs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AgentAvatarFallback, Avatar, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { IconTile } from '@/components/ui/icon-tile'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export type ProgramLifecycleAction =
  | 'activate'
  | 'pause'
  | 'reactivate'
  | 'suspend'
  | 'lift_suspension'
  | 'archive'
  | 'delete'

function formatAgentAddedAt(
  agent: AgentRecord,
  locale: string,
  t: (key: string) => string,
) {
  const raw = agent.activated_at ?? agent.invited_at ?? agent.created_at
  if (!raw) return t('programs.dialogs.assignment.dateUnknown')
  return new Date(raw).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function agentInitials(agent: AgentRecord): string {
  const n = agent.display_name?.trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
    }
    return n.slice(0, 2).toUpperCase()
  }
  return (agent.email ?? '?').slice(0, 2).toUpperCase()
}

function rewardItemsLabel(
  count: number,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return t('programs.dialogs.giftCount', { count })
}

function activeRewardItemCount(pack: ExchangePackRecord | null | undefined) {
  if (!pack) return 0
  return pack.active_items_count ?? pack.items.filter((item) => item.status === 'active').length
}

function isRewardPackAssignable(pack: ExchangePackRecord | null | undefined) {
  return activeRewardItemCount(pack) > 0
}

function firstAssignablePackId(packs: ExchangePackRecord[]) {
  return packs.find(isRewardPackAssignable)?.id ?? ''
}

const EMPTY_REWARD_PACK_MESSAGE_KEY = 'programs.dialogs.rewardPackEmptyActiveItems'

function getLifecycleCopy(
  t: (key: string) => string,
): Record<
  ProgramLifecycleAction,
  {
    title: string
    description: string
    alertTitle: string
    alertDescription: string
    confirmLabel: string
    destructive?: boolean
  }
> {
  return {
    activate: {
      title: t('programs.dialogs.lifecycle.activate.title'),
      description: t('programs.dialogs.lifecycle.activate.description'),
      alertTitle: t('programs.dialogs.lifecycle.activate.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.activate.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmActivate'),
    },
    pause: {
      title: t('programs.dialogs.pauseTitle'),
      description: t('programs.dialogs.pauseDescription'),
      alertTitle: t('programs.dialogs.lifecycle.pause.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.pause.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmPause'),
    },
    reactivate: {
      title: t('programs.dialogs.reactivateTitle'),
      description: t('programs.dialogs.reactivateDescription'),
      alertTitle: t('programs.dialogs.lifecycle.reactivate.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.reactivate.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmReactivate'),
    },
    suspend: {
      title: t('programs.dialogs.suspendTitle'),
      description: t('programs.dialogs.suspendDescription'),
      alertTitle: t('programs.dialogs.lifecycle.suspend.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.suspend.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmSuspend'),
      destructive: true,
    },
    lift_suspension: {
      title: t('programs.dialogs.lifecycle.liftSuspension.title'),
      description: t('programs.dialogs.lifecycle.liftSuspension.description'),
      alertTitle: t('programs.dialogs.lifecycle.liftSuspension.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.liftSuspension.alertDescription'),
      confirmLabel: t('programs.dialogs.lifecycle.liftSuspension.confirmLabel'),
    },
    archive: {
      title: t('programs.dialogs.archiveTitle'),
      description: t('programs.dialogs.archiveDescription'),
      alertTitle: t('programs.dialogs.lifecycle.archive.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.archive.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmArchive'),
      destructive: true,
    },
    delete: {
      title: t('programs.dialogs.deleteTitle'),
      description: t('programs.dialogs.deleteDescription'),
      alertTitle: t('programs.dialogs.lifecycle.delete.alertTitle'),
      alertDescription: t('programs.dialogs.lifecycle.delete.alertDescription'),
      confirmLabel: t('programs.dialogs.confirmDeleteAction'),
      destructive: true,
    },
  }
}

export function ProgramCashRulesDialog({
  open,
  program,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  program: ProgramRecord | null
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (pointsPerEuro: number | null) => Promise<void>
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!open) return
    setValue(program?.points_per_euro?.toString() ?? '')
  }, [open, program])

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('programs.dialogs.cashRulesTitle')}</DialogTitle>
          <DialogDescription>{t('programs.dialogs.cashRulesDescription')}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
          <div className="flex items-center gap-3">
            <IconTile icon={HandCoins} size="sm" className="bg-emerald-500 text-white" />
            <div>
              <p className="app-eyebrow text-emerald-800 dark:text-emerald-300">
                {t('programs.dialogs.cashEyebrow')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('programs.dialogs.cashHelper')}
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="program-cash-points">
              {t('programs.dialogs.pointsPerEuroLabel')}
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="program-cash-points"
                type="number"
                min="1"
                max="9999999"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder={t('programs.dialogs.pointsPerEuroPlaceholder')}
              />
              <Badge variant="outline" className="h-8 shrink-0 px-3">
                {t('programs.dialogs.pointsPerEuroBadge')}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('programs.dialogs.currentConfig')}{' '}
              <strong className="text-foreground">
                {program?.points_per_euro
                  ? t('programs.card.cash.configured', {
                      points: program.points_per_euro.toLocaleString(locale),
                      count: program.points_per_euro.toLocaleString(locale),
                    })
                  : t('programs.dialogs.currentConfigEmpty')}
              </strong>
            </p>
          </div>
        </div>

        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={isSubmitting || !value.trim()}
            onClick={() => void onSubmit(value.trim() ? Number(value) : null)}
          >
            {isSubmitting ? t('programs.dialogs.saving') : t('programs.dialogs.saveCash')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ProgramRewardPackDialog({
  open,
  program,
  packs,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  program: ProgramRecord | null
  packs: ExchangePackRecord[]
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (exchangePackId: string) => Promise<void>
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const [selectedPackId, setSelectedPackId] = useState('')
  const selectedPack = useMemo(
    () => packs.find((pack) => pack.id === selectedPackId) ?? null,
    [packs, selectedPackId],
  )

  useEffect(() => {
    if (!open) return
    setSelectedPackId(program?.exchange_pack?.id ?? firstAssignablePackId(packs))
  }, [open, packs, program])

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-h-[88vh] overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('programs.dialogs.rewardPackTitle')}</DialogTitle>
          <DialogDescription>{t('programs.dialogs.rewardPackDescription')}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <div className="flex items-center gap-3">
            <IconTile icon={Gift} size="sm" className="bg-amber-500 text-white" />
            <div className="min-w-0">
              <p className="app-eyebrow text-amber-900 dark:text-amber-300">
                {t('programs.dialogs.rewardPackEyebrow')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('programs.dialogs.rewardPackHelper')}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium text-foreground" htmlFor="program-reward-pack">
              {t('programs.dialogs.activePackLabel')}
            </label>
            <Select value={selectedPackId} onValueChange={setSelectedPackId}>
              <SelectTrigger id="program-reward-pack" className="mt-2">
                <SelectValue placeholder={t('programs.dialogs.rewardPackPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {packs.map((pack) => (
                  <SelectItem key={pack.id} value={pack.id} disabled={!isRewardPackAssignable(pack)}>
                    {isRewardPackAssignable(pack)
                      ? pack.name
                      : t('programs.dialogs.rewardPackInactiveLabel', { name: pack.name })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 max-h-[38vh] overflow-y-auto rounded-md bg-background p-3 text-sm">
            {selectedPack ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate font-medium text-foreground">{selectedPack.name}</span>
                  <Badge variant="outline" size="xs" className="shrink-0">
                    {rewardItemsLabel(activeRewardItemCount(selectedPack), t)}
                  </Badge>
                </div>

                {isRewardPackAssignable(selectedPack) ? (
                  <div className="space-y-2">
                    {selectedPack.items.filter((item) => item.status === 'active').map((item, index) => (
                      <Item key={item.id} variant="outline" size="sm" className="bg-card/70">
                        <ItemMedia>
                          <div className="flex size-6 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
                            {index + 1}
                          </div>
                        </ItemMedia>
                        <ItemContent>
                          <ItemTitle>{item.title}</ItemTitle>
                          <ItemDescription>
                            <strong>{item.points_cost.toLocaleString(locale)} {t('common.pts')}</strong>
                          </ItemDescription>
                        </ItemContent>
                      </Item>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg bg-muted/30 p-3 text-sm text-muted-foreground">
                    {t(EMPTY_REWARD_PACK_MESSAGE_KEY)}
                  </p>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">{t('programs.dialogs.noRewardPackSelected')}</span>
            )}
          </div>
        </div>

        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={isSubmitting || !selectedPackId || !isRewardPackAssignable(selectedPack)}
            onClick={() => void onSubmit(selectedPackId)}
          >
            {isSubmitting ? t('programs.dialogs.saving') : t('programs.dialogs.savePack')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ProgramAssignmentDialog({
  open,
  program,
  agents,
  assignments,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  program: ProgramRecord | null
  agents: AgentRecord[]
  assignments: AssignedAgent[]
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (agentIds: string[]) => Promise<void>
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const assignmentAgentIds = useMemo(
    () => assignments.map((assignment) => assignment.agent?.id).filter((value): value is string => Boolean(value)),
    [assignments],
  )
  const lockedAssignedAgentIds = useMemo(
    () => new Set(
      assignments
        .filter((assignment) => assignment.has_prospects_in_program)
        .map((assignment) => assignment.agent?.id)
        .filter((value): value is string => Boolean(value)),
    ),
    [assignments],
  )
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [hasEdited, setHasEdited] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedAgentIds(assignmentAgentIds)
    setHasEdited(false)
  }, [assignmentAgentIds, open])

  const effectiveSelectedAgentIds = hasEdited ? selectedAgentIds : assignmentAgentIds

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-h-[88vh] overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('programs.dialogs.assignAgentsTitle', {
              suffix: program ? ` ${t('common.to')} ${program.name}` : '',
            })}
          </DialogTitle>
          <DialogDescription>{t('programs.dialogs.assignAgentsDescription')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[56vh] space-y-2 overflow-y-auto pr-1">
          <TooltipProvider>
            {agents.length ? agents.map((agent) => {
              const isAssigned = effectiveSelectedAgentIds.includes(agent.id)
              const isLockedAssigned = isAssigned && lockedAssignedAgentIds.has(agent.id)
              const toggleAssignment = () => {
                if (isLockedAssigned) return
                setHasEdited(true)
                setSelectedAgentIds((current) => {
                  const base = hasEdited ? current : assignmentAgentIds
                  if (base.includes(agent.id)) {
                    return base.filter((id) => id !== agent.id)
                  }
                  return [...base, agent.id]
                })
              }
              const checkbox = (
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={isAssigned}
                  disabled={isLockedAssigned}
                  onChange={(event) => {
                    const checked = event.target.checked
                    if (isLockedAssigned) return
                    setHasEdited(true)
                    setSelectedAgentIds((current) => {
                      const base = hasEdited ? current : assignmentAgentIds
                      if (checked) {
                        if (base.includes(agent.id)) return base
                        return [...base, agent.id]
                      }
                      return base.filter((id) => id !== agent.id)
                    })
                  }}
                />
              )

              return (
                <button
                  key={agent.id}
                  type="button"
                  className="block w-full text-left"
                  onClick={toggleAssignment}
                  disabled={isLockedAssigned}
                >
                  <Item variant="outline" className={isLockedAssigned ? 'opacity-85' : undefined}>
                    <ItemMedia>
                      <Avatar className="size-10">
                        <AvatarImage src={agent.avatar_url ?? undefined} alt={agent.display_name ?? agent.email ?? t('common.agent')} />
                        <AgentAvatarFallback seed={agent.id}>{agentInitials(agent)}</AgentAvatarFallback>
                      </Avatar>
                    </ItemMedia>
                    <ItemContent>
                      <ItemTitle>{agent.display_name ?? agent.email ?? t('common.unknown')}</ItemTitle>
                      <ItemDescription>
                        {agent.email ?? t('programs.dialogs.assignment.emailUnavailable')} · {t('programs.dialogs.assignment.addedOn')} {formatAgentAddedAt(agent, locale, t)}
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      {isLockedAssigned ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex cursor-not-allowed opacity-70"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {checkbox}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('programs.dialogs.lockedAgentTooltip')}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span onClick={(event) => event.stopPropagation()}>{checkbox}</span>
                      )}
                    </ItemActions>
                  </Item>
                </button>
              )
            }) : (
              <p className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                {t('programs.dialogs.assignment.noAvailableAgents')}
              </p>
            )}
          </TooltipProvider>
        </div>

        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={isSubmitting || !program}
            onClick={() => void onSubmit(effectiveSelectedAgentIds)}
          >
            {isSubmitting ? t('programs.dialogs.saving') : t('programs.dialogs.saveAssignments')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ProgramLifecycleConfirmDialog({
  action,
  isSubmitting,
  onClose,
  onConfirm,
}: {
  action: { type: ProgramLifecycleAction; program: ProgramRecord } | null
  isSubmitting: boolean
  onClose: () => void
  onConfirm: (action: ProgramLifecycleAction, program: ProgramRecord) => Promise<void>
}) {
  const { t } = useTranslation()
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const lifecycleCopy = useMemo(() => getLifecycleCopy(t), [t])
  const copy = action ? lifecycleCopy[action.type] : lifecycleCopy.pause

  useEffect(() => {
    if (!action) setDeleteConfirmName('')
  }, [action])

  return (
    <Dialog open={Boolean(action)} onOpenChange={(isOpen) => { if (!isOpen && !isSubmitting) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <Alert
          variant={copy.destructive ? 'destructive' : 'default'}
          className={cn(
            !copy.destructive && action?.type === 'activate'
              ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-300'
              : undefined,
            !copy.destructive && action?.type !== 'activate'
              ? 'border-amber-500/25 bg-amber-500/10 text-amber-900 dark:text-amber-300'
              : undefined,
          )}
        >
          {copy.destructive ? <AlertTriangleIcon /> : action?.type === 'activate' ? <CheckCircle2Icon /> : <InfoIcon />}
          <AlertTitle>{copy.alertTitle}</AlertTitle>
          <AlertDescription>{copy.alertDescription}</AlertDescription>
        </Alert>

        {action?.type === 'delete' ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('programs.dialogs.expectedName')} : <strong>{action.program.name}</strong>
            </p>
            <Input
              value={deleteConfirmName}
              onChange={(event) => setDeleteConfirmName(event.target.value)}
              placeholder={t('programs.dialogs.deletePlaceholder')}
            />
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={copy.destructive ? 'destructive' : 'default'}
            disabled={
              isSubmitting ||
              !action ||
              (action.type === 'delete' && deleteConfirmName.trim() !== action.program.name)
            }
            onClick={() => {
              if (!action) return
              void onConfirm(action.type, action.program)
            }}
          >
            {isSubmitting ? t('programs.dialogs.processing') : copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
